import React, { useMemo, useState } from "react";
import { ArrowRight, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ModulePreview } from "./ModulePreview";
import { MODULES, MATERIAL_LABELS, caixaDepth, defaultMaterials, defaultValues, topDrawers } from "../lib/modules";
import { THICKNESSES, tamponPieces } from "../lib/pieces";
import { autoEdge } from "./PieceDialog";
import { ColorInput } from "./ColorInput";

const MaterialRow = ({ id, label, mat, onChange, noThickness, fixed }) => (
  <div data-testid={`module-material-${id}`} className="grid grid-cols-[110px_1fr_1fr] gap-2 items-center">
    <div>
      <p className="text-xs font-semibold">{label}</p>
      {fixed && <p data-testid={`module-material-${id}-fixed`} className="mt-1 rounded-md border border-[#2D333F] px-2 py-1 font-mono text-xs text-[#9CA3AF]">{fixed} mm (fixo)</p>}
      {!noThickness && !fixed && <Select value={String(mat.thickness)} onValueChange={(v) => onChange({ ...mat, thickness: Number(v) })}>
        <SelectTrigger data-testid={`module-material-${id}-thickness`} className="h-8 mt-1 bg-[#0F1115] border-[#2D333F] text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>{THICKNESSES.map((t) => <SelectItem key={t} value={String(t)}>{t} mm</SelectItem>)}</SelectContent>
      </Select>}
    </div>
    <ColorInput testId={`module-material-${id}-mdf`} listId="mdf-colors" value={mat.mdf_color} onChange={(v) => onChange({ ...mat, mdf_color: v, ...(id === "back" ? {} : { edge_color: autoEdge(v) }) })} placeholder="Cor / nome do MDF" className="h-9 bg-[#0F1115] border-[#2D333F] text-sm" />
    {id === "back" ? <span /> : (
      <ColorInput testId={`module-material-${id}-edge`} listId="edge-colors" value={mat.edge_color} onChange={(v) => onChange({ ...mat, edge_color: v })} placeholder="Fita de borda" className="h-9 bg-[#0F1115] border-[#2D333F] text-sm" />
    )}
  </div>
);

const BayRows = ({ mod, v, clamped, setV }) => {
  const rows = [...new Set(mod.params.filter((p) => p.row && (!p.when || p.when(clamped))).map((p) => p.row))];
  if (!rows.length) return null;
  return (
    <div data-testid="module-bay-rows" className="space-y-2">
      {rows.map((r) => (
        <div key={r} data-testid={`module-bay-row-${r}`} className="rounded-md border border-[#2D333F] p-2.5 space-y-1.5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-[#E67E22]">Vão {r}</p>
          <div className="grid grid-cols-[1fr_1fr_1.6fr] gap-2">
            {mod.params.filter((p) => p.row === r).map((p) => (
              <div key={p.key} className="space-y-1">
                <Label className="text-[10px] text-[#9CA3AF] whitespace-nowrap">{p.short}</Label>
                <Input data-testid={`module-param-${p.key}`} type="number" min={p.min} max={p.max} value={v[p.key]}
                  onChange={(e) => setV((o) => ({ ...o, [p.key]: e.target.value }))} className="h-8 bg-[#0F1115] border-[#2D333F] font-mono" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const ModuleDialog = ({ mod, defaults, onClose, onAdd }) => {
  const [v, setV] = useState(() => defaultValues(mod));
  const [M, setM] = useState(() => defaultMaterials(defaults));
  const clamped = useMemo(
    () => Object.fromEntries(mod.params.map((p) => [p.key, p.choices ? Number(v[p.key]) : Math.min(p.max, Math.max(p.min, Number(v[p.key]) || p.min))])),
    [v, mod]
  );
  const pieces = useMemo(() => mod.build(clamped, M), [clamped, mod, M]);
  const bad = pieces.find((p) => p.length < 15 || p.width < 15 || (p.notch?.enabled && (p.notch.length < 15 || p.notch.width < 15 || p.notch.length > p.length - 15 || p.notch.width > p.width - 15)));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="module-dialog" onEscapeKeyDown={(e) => { if (document.activeElement?.dataset?.colorOpen) e.preventDefault(); }} className="max-w-5xl bg-[#181B21] border-[#2D333F] text-[#F3F4F6] max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-xl">{mod.name}</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-[1fr_1fr] gap-6">
          <div className="space-y-4">
            <div className="rounded-lg border border-[#2D333F] bg-[#0F1115] blueprint-grid p-4">
              <ModulePreview kind={mod.key} values={clamped} className="w-full h-72" testId="module-dialog-preview" />
            </div>
            {[false, true].map((tp) => {
              const list = mod.params.filter((p) => !p.inlined && !p.row && (!p.when || p.when(clamped)) && (p.key === "tamp" || p.key.startsWith("tamp_")) === tp);
              if (!list.length) return null;
              return (
            <div key={String(tp)} data-testid={tp ? "module-tampon-section" : "module-params-grid"} className={tp ? "grid grid-cols-2 gap-3 rounded-lg border border-[#22D3EE]/40 p-3" : "grid grid-cols-2 gap-3"}>
              {list.map((p) => p.switch ? (
                <div key={p.key} data-testid={`module-switch-${p.key}-row`} className="col-span-2 flex items-center justify-between rounded-md border border-[#2D333F] px-3 py-2">
                  <div>
                    <p className="text-sm font-medium">{p.label}</p>
                    <p className="text-xs text-[#6B7280]">{p.hint}</p>
                  </div>
                  <Switch className="data-[state=checked]:bg-[#E67E22]" data-testid={`module-switch-${p.key}`} checked={Number(v[p.key]) === 1}
                    onCheckedChange={(c) => setV((o) => ({ ...o, [p.key]: c ? 1 : 0 }))} />
                </div>
              ) : p.choices ? (
                <div key={p.key} className="space-y-1 col-span-2">
                  <Label className="font-mono text-[11px] uppercase tracking-wider text-[#9CA3AF]">{p.label}</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {p.choices.map(([val, l], ci) => (
                      <React.Fragment key={val}>
                        <button type="button" data-testid={`module-choice-${p.key}-${val}`} onClick={() => setV((o) => ({ ...o, [p.key]: val }))}
                          className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${Number(v[p.key]) === val ? "border-[#E67E22] bg-[#E67E22]/15" : "border-[#2D333F] text-[#9CA3AF]"}`}>
                          {l}
                        </button>
                        {ci === 0 && p.inline && (() => {
                          const q = mod.params.find((x) => x.key === p.inline);
                          return (!q.when || q.when(clamped)) && (
                            <div className="flex items-center gap-1.5">
                              <span className="text-[11px] text-[#9CA3AF] whitespace-nowrap">Altura (mm)</span>
                              <Input data-testid={`module-param-${q.key}`} type="number" min={q.min} max={q.max} value={v[q.key]}
                                onChange={(e) => setV((o) => ({ ...o, [q.key]: e.target.value }))} className="h-8 w-24 bg-[#0F1115] border-[#2D333F] font-mono" />
                            </div>
                          );
                        })()}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ) : (
                <div key={p.key} className="space-y-1">
                  <Label className="font-mono text-[11px] uppercase tracking-wider text-[#9CA3AF]">{p.label}</Label>
                  <Input data-testid={`module-param-${p.key}`} type="number" min={p.min} max={p.max} value={v[p.key]}
                    onChange={(e) => setV((o) => ({ ...o, [p.key]: e.target.value }))} className="bg-[#0F1115] border-[#2D333F] font-mono" />
                  <p className="text-[10px] text-[#6B7280]">de {p.min} a {p.max}</p>
                </div>
              ))}
              {tp && clamped.tamp > 0 && M.tampon && (
                <div className="col-span-2">
                  <MaterialRow id="tampon" label="Cor do tamponamento" mat={M.tampon} noThickness
                    onChange={(m) => setM((o) => ({ ...o, tampon: m.mdf_color !== o.tampon.mdf_color ? { ...m, edge_color: autoEdge(m.mdf_color) } : m }))} />
                </div>
              )}
            </div>
              );
            })}
            <BayRows mod={mod} v={v} clamped={clamped} setV={setV} />
            {mod.info && <p data-testid="module-info" className="rounded-md border border-[#10B981]/40 bg-[#10B981]/10 px-3 py-2 text-xs text-[#10B981]">{mod.info(clamped)}</p>}
          </div>
          <div className="space-y-5">
            <div className="rounded-lg border border-[#2D333F] p-4 space-y-3">
              <p className="font-heading font-semibold">Materiais</p>
              {mod.materials.map((id) => (
                <MaterialRow key={id} id={id} label={mod.labels?.[id] || MATERIAL_LABELS[id]} mat={M[id]} noThickness={mod.key === "provencal"} fixed={id === "drawer" && M.slide === "oculta" ? 18 : null} onChange={(m) => setM((o) => ({ ...o, [id]: m }))} />
              ))}
              {mod.key === "balcao_inferior" && clamped.gaveta_pos === 1 && topDrawers(clamped.altura, clamped.gavetas) < clamped.gavetas && (
                <p data-testid="top-drawers-hint" className="text-[11px] text-[#F39C12]">Em cima cabem só {topDrawers(clamped.altura, clamped.gavetas)} gaveta(s) nesta altura (portas com no mínimo 200 mm).</p>
              )}
              {mod.backNote && (
                <p data-testid="box-depth-hint" className="text-[11px] text-[#9CA3AF]">Profundidade final {clamped.profundidade} mm → caixa {caixaDepth(mod.key, clamped, M)} mm{clamped.profundidade === caixaDepth(mod.key, clamped, M) ? "." : ` (descontado: ${[caixaDepth(mod.key, { ...clamped, fundo_tipo: 1, fundo: 0 }, M) !== clamped.profundidade && "porta", clamped.fundo_tipo === 0 && (mod.key !== "nicho" || clamped.fundo) && "fundo por fora"].filter(Boolean).join(" e ")}).`}</p>
              )}
              {mod.backNote && clamped.fundo_tipo === 1 && (
                <p data-testid="back-embed-hint" className="text-[11px] text-[#00E5FF]">Fundo embutido: rebaixo de {(M.back?.thickness || 6) + 1} mm de largura e 10 mm de profundidade nas laterais, base e topo (passa 5 mm além do início e do fim). Fundo e prateleiras são ajustados automaticamente.</p>
              )}
              {mod.slides && (
                <div data-testid="module-slide-options" className="space-y-1.5">
                  <p className="text-xs font-semibold">Corrediça das gavetas</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[["telescopica", "Telescópica", "Folga 13 mm por lado"], ["oculta", "Oculta", "Folga 5 mm por lado"]].map(([k, l, d]) => (
                      <button key={k} type="button" data-testid={`slide-${k}`} onClick={() => setM((o) => ({ ...o, slide: k }))}
                        className={`text-left rounded-md border px-3 py-2 transition-colors ${M.slide === k ? "border-[#E67E22] bg-[#E67E22]/15" : "border-[#2D333F] text-[#9CA3AF]"}`}>
                        <span className="block text-sm font-semibold">{l}</span><span className="text-[11px] opacity-70">{d}</span>
                      </button>
                    ))}
                  </div>
                  {M.slide === "oculta" && <p className="text-[11px] text-[#F39C12]">Caixa da gaveta fixa em 18 mm para corrediça oculta. Confira as medidas com o manual da corrediça oculta que você usa.</p>}
                </div>
              )}
            </div>
            <div>
              <p className="font-heading font-semibold mb-2">Peças geradas ({pieces.flatMap((p) => [p, ...tamponPieces(p)]).reduce((s, p) => s + p.qty, 0)})</p>
              <div className="rounded-lg border border-[#2D333F] divide-y divide-[#2D333F] text-sm">
                {pieces.flatMap((p) => [p, ...tamponPieces(p)]).map((p, i) => (
                  <div key={i} data-testid={`module-piece-${i}`} className="flex items-center justify-between gap-2 px-3 py-2">
                    <span className="truncate">{p.name}{p.tampon?.enabled && <span data-testid={`module-piece-tamp-${i}`} className="ml-2 rounded bg-[#E67E22]/20 px-1.5 py-0.5 text-[10px] font-semibold text-[#E67E22]">Tamp.</span>}{p.derived && <span className="ml-2 text-[10px] text-[#6B7280]">(tamponamento)</span>}</span>
                    <span className="font-mono text-xs text-[#9CA3AF] whitespace-nowrap">{p.qty}x · {p.length} x {p.width} · {p.thickness}mm · {p.mdf_color}</span>
                  </div>
                ))}
              </div>
              {mod.note && <p className="text-xs text-[#F39C12] mt-2">{mod.note}</p>}
              <p className="text-xs text-[#6B7280] mt-2">Depois de adicionar, você pode editar cada peça na lista.</p>
            </div>
          </div>
        </div>
        {bad && <p data-testid="module-invalid-hint" className="text-sm text-red-400">Medidas inválidas: “{bad.name}” fica com menos de 15 mm. Aumente os lados ou diminua as profundidades.</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="module-dialog-cancel">Cancelar</Button>
          <Button data-testid="module-add-btn" disabled={!!bad} onClick={() => onAdd(pieces)} className="bg-[#E67E22] hover:bg-[#D35400] text-black font-semibold">
            <Plus className="w-4 h-4 mr-1" /> Adicionar ao projeto
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ModulesGallery = ({ defaults, onAdd }) => {
  const [open, setOpen] = useState(null);
  return (
    <div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {MODULES.map((m, i) => (
          <button key={m.key} data-testid={`module-card-${m.key}`} onClick={() => setOpen(m)}
            className="group text-left rounded-xl border border-[#2D333F] bg-[#181B21] overflow-hidden hover:border-[#E67E22] transition-colors fade-up"
            style={{ animationDelay: `${i * 50}ms` }}>
            <div className="bg-[#0F1115] blueprint-grid p-4 h-52">
              <ModulePreview kind={m.key} values={defaultValues(m)} className="w-full h-full" testId={`module-card-preview-${m.key}`} />
            </div>
            <div className="p-4 flex items-end justify-between gap-3">
              <div>
                <p className="font-heading font-semibold">{m.name}</p>
                <p className="text-xs text-[#9CA3AF] mt-1">{m.desc}</p>
              </div>
              <ArrowRight className="w-5 h-5 text-[#E67E22] transition-transform group-hover:translate-x-1" />
            </div>
          </button>
        ))}
      </div>
      {open && (
        <ModuleDialog mod={open} defaults={defaults} onClose={() => setOpen(null)} onAdd={(ps) => { onAdd(ps, open.name); setOpen(null); }} />
      )}
    </div>
  );
};
